import { fetchContractorView, fetchContractorFirm, type ContractorView } from '@/lib/data/contractor-portal';
import { esc, fmtDate, setPageTitle, setTitleBase } from '@/lib/dom';
import { companyName, DEFAULT_COMPANY } from '@/lib/branding';
import { monogramFor } from '@/lib/host-branding';
import {
  ROOM_STATUS_LABEL, SPEC_STATUS_LABEL,
  SCHEDULE_STATUS_LABEL,
} from '@/lib/labels';
import { signPortalMedia } from '@/lib/data/portal-media';
import { hydratePortalImages } from '@/lib/portal-images';

const TRADE_LABEL: Record<string, string> = {
  plumbing: 'Plumbing', electrical: 'Electrical', carpentry: 'Carpentry',
  paint: 'Paint', tile: 'Tile', hvac: 'HVAC', drywall: 'Drywall',
  flooring: 'Flooring', cabinetry: 'Cabinetry', countertops: 'Countertops',
  landscape: 'Landscape', roofing: 'Roofing', general: 'General contracting',
  other: 'Other',
};

export async function renderContractorView(root: HTMLElement, token: string): Promise<void> {
  root.innerHTML = `<div class="share-shell"><div class="loading">Loading…</div></div>`;
  try {
    const [data, firm] = await Promise.all([
      fetchContractorView(token),
      fetchContractorFirm(token).catch(() => null),
    ]);
    if (!data) {
      // A dead link can't say whose it was: neutral, never a guessed firm.
      root.innerHTML = `
        <div class="share-shell">
          <div class="share-header">
            <div class="brand-name" style="font-family:var(--font-serif); font-size:18px">${esc(DEFAULT_COMPANY)}</div>
          </div>
          <main class="share-main">
            <div class="empty">
              <p>This contractor link is invalid or has expired.</p>
              <p class="muted">Please contact the designer for a new link.</p>
            </div>
          </main>
        </div>`;
      return;
    }
    const firmName = companyName(firm);
    setTitleBase(firmName);
    setPageTitle(`${data.project.name} · Contractor view`);
    paint(root, data, token, firmName);
  } catch (err) {
    root.innerHTML = `<div class="share-shell"><div class="auth-error" style="margin:32px">${esc(err instanceof Error ? err.message : 'Failed to load')}</div></div>`;
  }
}

function paint(root: HTMLElement, d: ContractorView, token: string, firmName: string): void {
  const firstName = (d.contact.name.split(/\s+/)[0] ?? d.contact.name);
  const tradeLabel = d.contact.trade ? (TRADE_LABEL[d.contact.trade] ?? d.contact.trade) : '';
  const nextDates = d.schedule
    .filter((s) => s.start_date)
    .sort((a, b) => (a.start_date! < b.start_date! ? -1 : 1));

  root.innerHTML = `
    <div class="share-shell">
      <div class="share-header">
        <div class="brand">
          <svg class="brand-mark" viewBox="0 0 64 64" aria-hidden="true">
            <circle cx="32" cy="32" r="30" stroke="#B89A6A" stroke-width="1.5" fill="none"/>
            <text x="32" y="40" text-anchor="middle" font-family="Cormorant Garamond, serif" font-size="26" fill="#1A1714" letter-spacing="2">${esc(monogramFor(firmName))}</text>
          </svg>
          <div>
            <div class="brand-name">${esc(firmName)}</div>
            <div class="brand-tag">Contractor portal</div>
          </div>
        </div>
      </div>

      <main class="share-main">
        <header class="client-hero">
          <div class="client-hero-body">
            <div class="eyebrow">${tradeLabel ? esc(tradeLabel) + ' · ' : ''}${esc(d.project.client_name ?? '')}</div>
            <h1 class="client-hero-title">Welcome, ${esc(firstName)}</h1>
            <div class="client-hero-sub">${esc(d.project.name)}</div>
            ${d.project.address ? `<p class="client-hero-desc">${esc(d.project.address)}</p>` : ''}
          </div>
        </header>

        ${nextDates.length === 0 ? '' : `
          <section class="panel">
            <div class="panel-head"><h2>Your scheduled work</h2></div>
            <ul class="schedule-list">
              ${nextDates.map((s) => {
                const room = d.rooms.find((r) => r.id === s.room_id);
                return `<li class="punch-item">
                  <div style="flex:1">
                    <div style="font-weight:500">${esc(s.task)}</div>
                    <div class="muted" style="font-size:12px">
                      ${room ? esc(room.name) + ' · ' : ''}${fmtDate(s.start_date)}${s.end_date && s.end_date !== s.start_date ? ' → ' + fmtDate(s.end_date) : ''}
                    </div>
                    ${s.notes ? `<div style="font-size:13px;margin-top:6px">${esc(s.notes)}</div>` : ''}
                  </div>
                  <span class="chip chip-${esc(s.status)}">${esc(SCHEDULE_STATUS_LABEL[s.status])}</span>
                </li>`;
              }).join('')}
            </ul>
          </section>`}

        ${d.rooms.length === 0 ? `
          <div class="empty-inline">No rooms have been assigned to you yet. The designer will let you know when there's work scheduled.</div>
        ` : d.rooms.map((r) => renderRoom(r, d)).join('')}

        <footer class="share-footer">
          <div>Shared by ${esc(firmName)}</div>
          <div class="muted small">Read-only view — contact the designer with any questions.</div>
        </footer>
      </main>
    </div>
  `;

  void hydratePortalImages(root, (paths) => signPortalMedia('contractor', token, paths));
}

function renderRoom(r: ContractorView['rooms'][number], d: ContractorView): string {
  const roomSpecs = d.specs.filter((s) => s.room_id === r.id);
  const palette = d.palette.filter(
    (c) => c.room_id === r.id || c.room_id === null,
  );
  const imgs = d.room_images.filter((i) => i.room_id === r.id);
  const inspiration = imgs.filter((i) => i.kind === 'inspiration');
  const detail = imgs.filter((i) => i.kind === 'detail');
  const before = imgs.filter((i) => i.kind === 'before');
  const after = imgs.filter((i) => i.kind === 'after');

  return `
    <section class="panel">
      <div class="panel-head">
        <h2>${esc(r.name)}</h2>
        <span class="chip chip-${esc(r.status)}">${esc(ROOM_STATUS_LABEL[r.status])}</span>
      </div>
      <div class="muted" style="font-size:12px;margin-bottom:12px">${r.is_exterior ? 'Exterior' : 'Interior'}</div>
      ${r.notes ? `<p style="font-size:14px;margin-bottom:14px">${esc(r.notes)}</p>` : ''}

      ${roomSpecs.length === 0 ? '' : `
        <h4 class="muted" style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;margin:12px 0 8px">Specs</h4>
        <ul class="share-spec-list">${roomSpecs.map((s) => `
          <li>
            <span>${s.thumbnail_url ? `<img class="spec-thumb" data-storage-path="${esc(s.thumbnail_url)}" alt="" /> ` : ''}${esc(s.item)}${s.vendor ? ` <span class="muted">— ${esc(s.vendor)}</span>` : ''}${s.source_url ? ` <a href="${esc(s.source_url)}" target="_blank" rel="noopener">↗</a>` : ''}</span>
            <span class="chip chip-${esc(s.status)}">${esc(SPEC_STATUS_LABEL[s.status])}</span>
          </li>`).join('')}</ul>`}

      ${palette.length === 0 ? '' : `
        <h4 class="muted" style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;margin:16px 0 8px">Paint &amp; finishes</h4>
        <div class="palette-grid">
          ${palette.map((c) => `
            <div class="color-swatch" style="cursor:default">
              <span class="swatch" style="background:${esc(c.hex)}"></span>
              <span class="swatch-meta">
                <span class="swatch-name">${esc(c.name)}</span>
                <span class="muted">${esc(c.hex)}${c.code ? ` · ${esc(c.code)}` : ''}</span>
              </span>
            </div>`).join('')}
        </div>`}

      ${(before.length > 0 && after.length > 0) ? `
        <h4 class="muted" style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;margin:16px 0 8px">Before &amp; After</h4>
        <div class="before-after-pairs">
          ${Array.from({ length: Math.min(before.length, after.length) }, (_, i) => `
            <div class="before-after-pair">
              <figure><div class="ba-label">Before</div><img data-storage-path="${esc(before[i]!.storage_path)}" alt="" /></figure>
              <figure><div class="ba-label">After</div><img data-storage-path="${esc(after[i]!.storage_path)}" alt="" /></figure>
            </div>`).join('')}
        </div>` : ''}

      ${inspiration.length === 0 ? '' : `
        <h4 class="muted" style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;margin:16px 0 8px">Inspiration</h4>
        <div class="room-image-grid">${inspiration.map((i) => `<img class="room-image-thumb" data-storage-path="${esc(i.storage_path)}" alt="${esc(i.caption ?? '')}" />`).join('')}</div>`}

      ${detail.length === 0 ? '' : `
        <h4 class="muted" style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;margin:16px 0 8px">Details</h4>
        <div class="room-image-grid">${detail.map((i) => `<img class="room-image-thumb" data-storage-path="${esc(i.storage_path)}" alt="${esc(i.caption ?? '')}" />`).join('')}</div>`}
    </section>
  `;
}
